import { useState } from 'react';
import { useParams, useNavigate, useMatch } from 'react-router-dom';
import { Theme } from '@canvas-logic/clp-shared-ui';
import { LightDarkSwitch } from '@/ui-kit/components/LightDarkSwitch/LightDarkSwitch';
import { StyleGuideColors } from '@/pages/StyleGuide/sections/StyleGuideColors';
import { StyleGuideTypography } from '@/pages/StyleGuide/sections/StyleGuideTypography';
import { StyleGuideShadows } from '@/pages/StyleGuide/sections/StyleGuideShadows';
import { StyleGuideFullExample } from '@/pages/StyleGuide/sections/StyleGuideFullExample';
import {
  StyleGuideButtons,
  StyleGuideCardTypes,
  StyleGuideCardStates,
  StyleGuideCardGrid,
  StyleGuideContainers,
  StyleGuideIcons,
  StyleGuideInputs,
  StyleGuideOptions,
  StyleGuideOptionText,
  StyleGuideOther,
  StyleGuideSidebar,
} from '@/pages/StyleGuide/sections';
import { SectionErrorBoundary } from './SectionErrorBoundary';
import styles from './StyleGuidePage.module.css';

type Section =
  | 'colors'
  | 'typography'
  | 'shadows'
  | 'full-example'
  | 'containers'
  | 'options'
  | 'cards'
  | 'buttons'
  | 'icons'
  | 'inputs'
  | 'option-text'
  | 'other'
  | 'sidebar';

const designTokenSections: { key: Section; label: string }[] = [
  { key: 'colors', label: 'Color Scheme' },
  { key: 'typography', label: 'Typography' },
  { key: 'shadows', label: 'Shadows' },
];

const componentSections: { key: Section; label: string }[] = [
  { key: 'sidebar', label: 'Sidebar' },
  { key: 'cards', label: 'Cards' },
  { key: 'option-text', label: 'Option Text' },
  { key: 'buttons', label: 'Buttons' },
  { key: 'icons', label: 'Icons' },
  { key: 'inputs', label: 'Inputs' },
  { key: 'other', label: 'Other' },
];

const uiConfigSections: { key: Section; label: string }[] = [
  { key: 'full-example', label: 'Full Example' },
  { key: 'containers', label: 'Containers' },
  { key: 'options', label: 'Options' },
];

interface TabInfo {
  key: string;
  label: string;
  figmaUrl: string;
  figmaNodeId: string;
}

interface SectionMeta {
  figmaUrl: string;
  figmaNodeId: string;
  tabs?: TabInfo[];
}

const sectionMeta: Partial<Record<Section, SectionMeta>> = {
  sidebar: {
    figmaUrl:
      'https://www.figma.com/design/ommWmmf7fEaLgSJh7tB69Y/Canvas-Universal-Configurator--Copy-?node-id=1306-26264&m=dev',
    figmaNodeId: '1306:26264',
  },
  'option-text': {
    figmaUrl:
      'https://www.figma.com/design/0WKwaGa1yjJSbKHukww1P2/Canvas-Universal-Configurator--Copy-?node-id=3-3034&m=dev',
    figmaNodeId: '3:3034',
  },
  icons: {
    figmaUrl:
      'https://www.figma.com/design/0WKwaGa1yjJSbKHukww1P2/Canvas-Universal-Configurator--Copy-?node-id=367-5832&m=dev',
    figmaNodeId: '367:5832',
  },
  cards: {
    figmaUrl:
      'https://www.figma.com/design/0WKwaGa1yjJSbKHukww1P2/Canvas-Universal-Configurator--Copy-?node-id=3-2927&m=dev',
    figmaNodeId: '3:2927',
    tabs: [
      {
        key: 'types',
        label: 'Card Types',
        figmaUrl:
          'https://www.figma.com/design/0WKwaGa1yjJSbKHukww1P2/Canvas-Universal-Configurator--Copy-?node-id=3-2927&m=dev',
        figmaNodeId: '3:2927',
      },
      {
        key: 'states',
        label: 'Card States',
        figmaUrl:
          'https://www.figma.com/design/0WKwaGa1yjJSbKHukww1P2/Canvas-Universal-Configurator--Copy-?node-id=3-3227&m=dev',
        figmaNodeId: '3:3227',
      },
      {
        key: 'grid',
        label: 'Card Grid',
        figmaUrl:
          'https://www.figma.com/design/0WKwaGa1yjJSbKHukww1P2/Canvas-Universal-Configurator--Copy-?node-id=3-3149&m=dev',
        figmaNodeId: '3:3149',
      },
    ],
  },
  buttons: {
    figmaUrl:
      'https://www.figma.com/design/0WKwaGa1yjJSbKHukww1P2/Canvas-Universal-Configurator--Copy-?node-id=100-3827&m=dev',
    figmaNodeId: '100:3827',
    tabs: [
      {
        key: 'light',
        label: 'Light',
        figmaUrl:
          'https://www.figma.com/design/0WKwaGa1yjJSbKHukww1P2/Canvas-Universal-Configurator--Copy-?node-id=100-3827&m=dev',
        figmaNodeId: '100:3827',
      },
      {
        key: 'dark',
        label: 'Dark',
        figmaUrl:
          'https://www.figma.com/design/0WKwaGa1yjJSbKHukww1P2/Canvas-Universal-Configurator--Copy-?node-id=100-3950&m=dev',
        figmaNodeId: '100:3950',
      },
    ],
  },
  inputs: {
    figmaUrl:
      'https://www.figma.com/design/0WKwaGa1yjJSbKHukww1P2/Canvas-Universal-Configurator--Copy-?node-id=528-8899&m=dev',
    figmaNodeId: '528:8899',
  },
  other: {
    figmaUrl:
      'https://www.figma.com/design/0WKwaGa1yjJSbKHukww1P2/Canvas-Universal-Configurator--Copy-?node-id=4-5798&m=dev',
    figmaNodeId: '4:5798',
  },
};

const sectionLabels: Record<Section, string> = {
  colors: 'Color Scheme',
  typography: 'Typography',
  shadows: 'Shadows',
  'full-example': 'Full Example',
  containers: 'Containers',
  options: 'Options',
  cards: 'Cards',
  buttons: 'Buttons',
  icons: 'Icons',
  inputs: 'Inputs',
  'option-text': 'Option Text / Group',
  other: 'Other',
  sidebar: 'Sidebar',
};

const isViewportSection = (section: Section) =>
  section !== 'colors' && section !== 'typography' && section !== 'shadows';

const isUiConfigSection = (section: Section) =>
  section === 'full-example' || section === 'containers' || section === 'options';

const allSections: Section[] = [
  ...designTokenSections.map((s) => s.key),
  ...componentSections.map((s) => s.key),
  ...uiConfigSections.map((s) => s.key),
];

const isValidSection = (value: string | undefined): value is Section =>
  !!value && allSections.includes(value as Section);

export const StyleGuidePage = () => {
  const { section: sectionParam, tab: tabParam } = useParams<{ section?: string; tab?: string }>();
  const navigate = useNavigate();
  const isStorybook = !!useMatch('/storybook/*');
  const basePath = isStorybook ? '/storybook' : '/style-guide';

  const activeSection: Section = isValidSection(sectionParam) ? sectionParam : 'colors';
  const meta = sectionMeta[activeSection];
  const activeTab = meta?.tabs?.find((t) => t.key === tabParam) ? tabParam! : meta?.tabs?.[0]?.key ?? '';

  const [viewportSize, setViewportSize] = useState<'xs' | 'sm' | 'md' | 'lg'>('sm');

  const handleSectionChange = (section: Section) => {
    const sectionMetaInfo = sectionMeta[section];
    const firstTab = sectionMetaInfo?.tabs?.[0]?.key;
    navigate(firstTab ? `${basePath}/${section}/${firstTab}` : `${basePath}/${section}`);
  };

  const handleTabChange = (tab: string) => {
    navigate(`${basePath}/${activeSection}/${tab}`);
  };

  return (
    <div className={`configurator-styles ${styles.layout}`}>
      <Theme />
      <aside className={styles.sidebar}>
        <h1 className={styles.sidebarTitle}>Style Guide</h1>

        <p className={styles.sectionHeader}>Design Tokens</p>
        <ul className={styles.navList}>
          {designTokenSections.map(({ key, label }) => (
            <li key={key}>
              <button
                className={`${styles.navItem} ${activeSection === key ? styles.navItemActive : ''}`}
                onClick={() => handleSectionChange(key)}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>

        <p className={styles.sectionHeader}>Components</p>
        <ul className={styles.navList}>
          {componentSections.map(({ key, label }) => (
            <li key={key}>
              <button
                className={`${styles.navItem} ${activeSection === key ? styles.navItemActive : ''}`}
                onClick={() => handleSectionChange(key)}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>

        <p className={styles.sectionHeader}>UI Config</p>
        <ul className={styles.navList}>
          {uiConfigSections.map(({ key, label }) => (
            <li key={key}>
              <button
                className={`${styles.navItem} ${activeSection === key ? styles.navItemActive : ''}`}
                onClick={() => handleSectionChange(key)}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>

        {isUiConfigSection(activeSection) && (
          <div className={styles.viewportSelector}>
            <span className={styles.viewportLabel}>Viewport:</span>
            <div className={styles.viewportButtons}>
              {(['xs', 'sm', 'md', 'lg'] as const).map((size) => (
                <button
                  key={size}
                  className={`${styles.viewportButton} ${viewportSize === size ? styles.viewportButtonActive : ''}`}
                  onClick={() => setViewportSize(size)}
                >
                  {size.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className={styles.themeToggle}>
          <span>Theme:</span>
          <LightDarkSwitch />
        </div>
      </aside>

      {isViewportSection(activeSection) ? (
        <main
          className={styles.contentViewport}
          data-viewport={isUiConfigSection(activeSection) ? viewportSize : undefined}
        >
          {(() => {
            const meta = sectionMeta[activeSection];
            if (!meta) return null;
            const currentTab = meta.tabs?.find((t) => t.key === activeTab);
            const figmaUrl = currentTab?.figmaUrl ?? meta.figmaUrl;
            const figmaNodeId = currentTab?.figmaNodeId ?? meta.figmaNodeId;
            const figmaLabel = currentTab?.label ?? sectionLabels[activeSection];
            return (
              <div className={styles.contentHeader}>
                {meta.tabs && (
                  <div className={styles.tabBar}>
                    {meta.tabs.map((tab) => (
                      <button
                        key={tab.key}
                        className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
                        onClick={() => handleTabChange(tab.key)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}
                <div className={styles.figmaMeta}>
                  <span className={styles.figmaMetaLabel}>Figma:</span>
                  <a href={figmaUrl} target="_blank" rel="noopener noreferrer" className={styles.figmaMetaLink}>
                    {figmaLabel} ↗
                  </a>
                  <span className={styles.figmaMetaNodeId}>node-id: {figmaNodeId}</span>
                </div>
              </div>
            );
          })()}
          <div className={styles.contentInner}>
            <SectionErrorBoundary sectionName={sectionLabels[activeSection]}>
              {activeSection === 'full-example' && <StyleGuideFullExample />}
              {activeSection === 'sidebar' && <StyleGuideSidebar />}
              {activeSection === 'containers' && <StyleGuideContainers />}
              {activeSection === 'options' && <StyleGuideOptions />}
              {activeSection === 'cards' && activeTab === 'types' && <StyleGuideCardTypes />}
              {activeSection === 'cards' && activeTab === 'states' && <StyleGuideCardStates />}
              {activeSection === 'cards' && activeTab === 'grid' && <StyleGuideCardGrid />}
              {activeSection === 'buttons' && <StyleGuideButtons mode={(activeTab || 'light') as 'light' | 'dark'} />}
              {activeSection === 'icons' && <StyleGuideIcons />}
              {activeSection === 'option-text' && <StyleGuideOptionText />}
              {activeSection === 'inputs' && <StyleGuideInputs />}
              {activeSection === 'other' && <StyleGuideOther />}
            </SectionErrorBoundary>
          </div>
        </main>
      ) : (
        <main className={styles.content}>
          <SectionErrorBoundary sectionName={sectionLabels[activeSection]}>
            {activeSection === 'colors' && <StyleGuideColors />}
            {activeSection === 'typography' && <StyleGuideTypography />}
            {activeSection === 'shadows' && <StyleGuideShadows />}
          </SectionErrorBoundary>
        </main>
      )}
    </div>
  );
};
